import { formatMonthYearPeriod, MonthYearPeriod } from '@common/neo.tax';
import { AlertModalLayout, Button, GeneralFormError, Stack, Typography } from '@ui-components/neo.tax';
import { ReactElement, useState } from 'react';

import { StyledModal } from './confirm-short-year-modal.styled';

interface ConfirmShortYearChangeModalProps {
  close: () => void;
  isOpen: boolean;
  onConfirm: () => Promise<void>;
  loading: boolean;
  shortYearPeriod: MonthYearPeriod;
}

export const ConfirmShortYearChangeModal = ({
  close,
  isOpen,
  loading,
  onConfirm,
  shortYearPeriod,
}: ConfirmShortYearChangeModalProps): ReactElement => {
  const [errorMessage, setErrorMessage] = useState<string | ReactElement>();

  const formattedMonthYearPeriod = formatMonthYearPeriod(shortYearPeriod);

  const onSubmit = async (): Promise<void> => {
    try {
      setErrorMessage(undefined);
      await onConfirm();
      close();
    } catch (error) {
      setErrorMessage(
        (errorMessage) =>
          errorMessage ||
          (error instanceof Error && error.message) ||
          'An unexpected error occurred. Please try again.',
      );
    }
  };

  const onClickCancel = (): void => {
    close();
    setErrorMessage(undefined);
  };

  return (
    <StyledModal isOpen={isOpen} setIsOpen={close}>
      <AlertModalLayout
        title="Change Short Tax Year"
        copy=""
        trailingContent={
          <>
            <Button size="base" variant="white" rounded="regular" onClick={onClickCancel}>
              Cancel
            </Button>
            <Button disabled={loading} rounded="regular" size="base" type="submit" variant="rsbry" onClick={onSubmit}>
              {loading ? 'Submitting...' : 'Confirm'}
            </Button>
          </>
        }
      >
        <Stack direction="column">
          <Typography as="p">
            Changing short tax year dates will cause large changes to the credit calculation process. Please confirm
            your dates are correct.
          </Typography>
          <br />
          <Typography as="p">
            Short Tax Year:{' '}
            <Typography as="b">
              {formattedMonthYearPeriod.start} - {formattedMonthYearPeriod.end}
            </Typography>
          </Typography>
        </Stack>
        <GeneralFormError error={!!errorMessage}>{errorMessage}</GeneralFormError>
      </AlertModalLayout>
    </StyledModal>
  );
};
